import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import {
  ApiErrorStatusResponse,
  TokenVaultVerification,
  UserDto,
  UserTwoFATokenVaultActions,
} from '@workspace/data-types';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, tap } from 'rxjs';
import { ObfuscateEmailPipe } from '../../../../pipes/obfuscate-email.pipe';
import { ObfuscateMobileNumberPipe } from '../../../../pipes/obfuscate-mobile-number.pipe';
import { AlertSnackBarService } from '../../../../services/alert-snackbar.service';
import { EnumsNiceLabelUtils } from '../../../../utils/enums-nice-label.utils';
import { codeFormatValidator } from '../../../../validators/code-format.validator';
import { AbstractDialogAuraComponent, DialogData } from '../../../abstract/dialog.abstract.component';
import { AuraInputCodeVerificationComponent } from '../../../inputs/code-verification/code-verification.component';
import { DialogLayoutAuraComponent } from '../../../layouts/dialog/dialog.component';

interface TabItem {
  key: string;
  label: string;
  icon?: string;
  step?: StepVerification;
}

interface StepVerification {
  type: 'stepper' | 'email_code' | 'sms_code';
  title: string;
  description?: string;
  tabs?: TabItem[];
}

export interface UserTwoFAVerificationDialogData extends DialogData {
  action: UserTwoFATokenVaultActions;
}

@Component({
  selector: 'aura-user-two-fa-verification-dialog',
  templateUrl: './user-two-FA-verification.component.html',
  imports: [
    DialogLayoutAuraComponent,
    MatButtonModule,
    MatIconModule,
    MatDivider,
    AuraInputCodeVerificationComponent,
  ],
})
export class UserTwoFAVerificationDialogAuraComponent extends AbstractDialogAuraComponent<UserTwoFAVerificationDialogData> {
  private readonly userStore = inject(UserStore);
  private readonly obfuscateEmail = new ObfuscateEmailPipe();
  private readonly obfuscateMobileNumber = new ObfuscateMobileNumberPipe();
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private obfuscatedUserEmail: string;
  private obfuscatedUserMobile: string;

  codeControl = new FormControl('', [Validators.required, codeFormatValidator]);
  currentUser = signal<UserDto | null>(null);
  steps = signal<StepVerification | null>(null);
  currentStep = signal<StepVerification | null>(null);
  currentVerificationMode = signal<TokenVaultVerification | null>(null);
  stepHistory = signal<StepVerification[]>([]);
  resendCount = signal<number>(0);

  private alertSnackbar = inject(AlertSnackBarService);

  constructor() {
    super();
    const email = this.userStore?.currentUser()?.contactInfos.email ?? '';
    const mobile = this.userStore?.currentUser()?.contactInfos.mobileNumber ?? '';
    this.obfuscatedUserEmail = this.obfuscateEmail.transform(email);
    this.obfuscatedUserMobile = this.obfuscateMobileNumber.transform(mobile);
    this.steps.set({
      title: "Confirmez qu'il s'agit bien de vous",
      description: 'Choisissez comment recevoir un code de vérification.',
      type: 'stepper',
      tabs: [
        {
          key: 'email',
          label: 'Email',
          icon: 'email',
          step: {
            title: 'Par quel moyen voulez-vous recevoir votre code ?',
            type: 'stepper',
            tabs: [
              {
                key: 'verification',
                label: this.obfuscatedUserEmail,
                step: {
                  title: 'Saisissez votre code de vérification',
                  type: 'email_code',
                  description: `Nous l'avons envoyé à ${this.obfuscatedUserEmail}`,
                },
              },
            ],
          },
        },
        {
          key: 'mobile',
          label: 'Téléphone portable',
          icon: 'sms',
          step: {
            title: 'Par quel moyen voulez-vous recevoir votre code ?',
            type: 'stepper',
            tabs: [
              {
                key: 'verification',
                label: this.obfuscatedUserMobile,
                step: {
                  title: 'Saisissez votre code de vérification',
                  type: 'sms_code',
                  description: `Nous l'avons envoyé à ${this.obfuscatedUserMobile}`,
                },
              },
            ],
          },
        },
        //    { key: 'sms', label: 'SMS', icon: 'sms' },
      ],
    });
    this.currentStep.set(this.steps());

    effect(() => {
      const currentStep = this.currentStep();
      if (currentStep?.type === 'email_code') {
        this.currentVerificationMode.set(TokenVaultVerification.EMAIL);
        this.sendNewTwoFAToken();
      } else if (currentStep?.type === 'sms_code') {
        this.currentVerificationMode.set(TokenVaultVerification.SMS);
        this.sendNewTwoFAToken();
      }
    });
  }

  onTabClick(tab: TabItem) {
    const nextStep = tab.step;
    if (nextStep) {
      // push current step to history
      const current = this.currentStep();
      if (current) {
        this.stepHistory.update((history) => [...history, current]);
      }
      // set next step
      this.currentStep.set(nextStep);
    }
  }

  onBack() {
    const history = this.stepHistory();
    if (history.length > 0) {
      const previousStep = history[history.length - 1];
      this.stepHistory.set(history.slice(0, -1));
      this.currentStep.set(previousStep);
    }
  }

  firstStep() {
    this.currentStep.set(this.steps());
    this.stepHistory.set([]);
  }

  get canGoBack(): boolean {
    return this.stepHistory().length > 0;
  }

  verifyToken(code: string) {
    const mode = this.currentVerificationMode();
    if (!mode) return;

    this.api
      .tokenVaults()
      .users()
      .twoFA()
      .verifyToken({
        action: this.data.action,
        token: code,
        verification: mode,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((success) => {
          this.alertSnackbar.info({
            title: 'Vérification réussi',
            message: 'Vous pouvez désormais effectuer votre action.',
          });
          this.dialogRef.close(success);
        }),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  sendNewTwoFAToken() {
    const mode = this.currentVerificationMode();
    if (!mode) return;

    this.api
      .tokenVaults()
      .users()
      .twoFA()
      .newToken({
        action: this.data.action,
        verification: mode,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() =>
          this.alertSnackbar.info({
            title: "Vérification de l'utilisateur",
            message: 'Un nouveau code vient de vous être envoyé.',
          }),
        ),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  resend() {
    setTimeout(() => {
      this.resendCount.update((v) => v + 1);
    }, 15000);
    this.sendNewTwoFAToken();
  }
}
